import { notificationApi } from '@mathflat/domain/Notification'
import { differenceInDays } from 'date-fns'
import Cookies from 'js-cookie'
import { observer } from 'mobx-react'
import { type FC, useEffect } from 'react'

import { ReqPostMessageRecord, ResPostMessageRecord } from '~/@common/constants/iframe/iframe'
import {
  PASSWORD_CHANGE_MODAL_ORDERING_NUMBER,
  PUSH_NOTIFICATION_PERMISSION_MODAL_ORDERING_NUMBER,
} from '~/@common/constants/ui/modalOrdering'
import { useCallbackOnVisibilityChange } from '~/@common/hooks/useCallbackOnVisibilityChange'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { webviewService } from '~/@common/services'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { localStorageService } from '~/@common/services/storage.service'
import ForcePasswordChangeModal from '~/@pages/@widgets/ForcePasswordChangeModal'
import PasswordChangeModal from '~/@pages/@widgets/PasswordChangeModal'

import PushNotificationPermissionModal from './@widgets/PushNotificationPermissionModal/PushNotificationPermissionModal'
import ParentHomePage from './ParentHome'
import RestrictionHomePage from './RestrictionHome'
import SimpleHomePage from './SimpleHome'

const HomePage: FC = observer(() => {
  useCallbackOnVisibilityChange()
  const { isMobile } = useStudentAppMediaQuery()

  useEffect(() => {
    window?.parent?.postMessage(ReqPostMessageRecord.PAGE_RENDERED_AFTER_LOGIN, '*')

    const handleGetMessage = (e) => {
      if (e?.data?.type === ResPostMessageRecord.LOG_OUT.type) {
        authService.logout()
        return
      }
    }

    window.addEventListener('message', handleGetMessage)
    return () => {
      window.removeEventListener('message', handleGetMessage)
    }
  }, [])

  useEffect(() => {
    const checkIsOpenedInIframe = () => {
      try {
        return window.self !== window.top
      } catch (err) {
        return true
      }
    }

    if (checkIsOpenedInIframe()) return

    openPasswordChangePopup(isMobile)
    openForcePasswordChangePopup()
    openPushNotificationPermissionModal()
  }, [])

  // ─── 권한별 분기 ───
  const { isParent } = commonRepo.permission

  // 학부모
  if (isParent) return <ParentHomePage />
  // 초등학교 (데스크탑) — 별도 레이아웃
  if (commonRepo.isElementarySchoolflat && !isMobile) {
    return <SimpleHomePage />
  }
  return <RestrictionHomePage />
})

export default HomePage

// ─── 비밀번호 변경 / 푸시 알림 모달 헬퍼 (공통) ───
const openPasswordChangePopup = (isMobile: boolean) => {
  if (
    localStorageService.user.isInitialPassword &&
    Cookies.get(
      `doNotShowAgainForThreeMonths-password_change-${localStorageService.user.studentId}`,
    ) !== 'true'
  ) {
    modalService.openModal(
      {
        content: <PasswordChangeModal isInitial />,
        header: '비밀번호 변경',
      },
      {
        modalName: '최초 진입 비밀번호 변경 모달',
        hasCloseButton: isMobile,
        headerOption: {
          hasBottomBorder: false,
        },
        orderingNumber: PASSWORD_CHANGE_MODAL_ORDERING_NUMBER,
      },
    )
    setTimeout(() => {
      localStorageService.clearIsInitialPassword()
    })
  }
}

const openForcePasswordChangePopup = () => {
  const isPasswordChangedRequired = localStorageService.user.isPasswordChangedRequired

  if (isPasswordChangedRequired) {
    modalService.openModal(
      {
        content: <ForcePasswordChangeModal />,
        header: '안전한 이용을 위해 비밀번호를 변경해주세요',
      },
      {
        modalName: '비밀번호 강제 변경',
        isCloseable: false,
        headerOption: {
          hasBottomBorder: false,
          style: { wordBreak: 'keep-all', textAlign: 'center' },
        },
        orderingNumber: PASSWORD_CHANGE_MODAL_ORDERING_NUMBER,
      },
    )
  }
}

const PUSH_NOTIFICATION_RETRY_DAYS = 30

const openPushNotificationPermissionModal = async () => {
  // PUSH restriction 차단 시 모달 미노출
  if (!commonRepo.permission.byFeature.isAllowed('PUSH')) return
  if (!webviewService.isApp || !webviewService.isPushNotificationAvailableVersion) return

  const pushEnabled = (await notificationApi.getPushNotificationSetting()).pushEnabled
  if (pushEnabled) return

  const deniedAt = localStorageService.pushNotification.deniedTimestamp
  if (deniedAt) {
    const daysSinceDenied = differenceInDays(new Date(), new Date(deniedAt))
    if (daysSinceDenied < PUSH_NOTIFICATION_RETRY_DAYS) return
  }

  modalService.openModal(<PushNotificationPermissionModal />, {
    modalName: '푸시 알림 동의 모달',
    hasCloseButton: false,
    isCloseable: false,
    orderingNumber: PUSH_NOTIFICATION_PERMISSION_MODAL_ORDERING_NUMBER,
  })
}
